/**
 * Glyphes de la refonte « Nourri » — SVG en ligne, relevés sur le prototype.
 *
 * **Pourquoi ce fichier existe.** Les six composants de la bibliothèque
 * décrivent des *contenants* — carte, bouton, barre, anneau, champ, badge. Le
 * prototype, lui, pose aussi une vingtaine de tracés : l'icône de chaque repas,
 * les chevrons, l'étoile de favori, la loupe, les six pastilles des Réglages.
 * Sans eux, les écrans migrés seraient la bonne structure avec les mauvais
 * signes.
 *
 * ⚠️ **Ce ne sont pas des styles, ce sont des dessins**, et ils sont recopiés
 * du prototype tracé pour tracé — pas redessinés. Si un glyphe paraît faux,
 * c'est `design/nutri-redesign.html` qu'il faut relire, pas ce fichier qu'il
 * faut corriger au jugé.
 *
 * **Pourquoi pas une bibliothèque d'icônes** : la réponse de l'étape 5 n'a pas
 * changé. Vingt glyphes ne justifient ni une dépendance, ni les 20 à 60 ko
 * qu'elle pèserait, ni un chargement réseau sur le chemin critique. Écrits ici,
 * ils héritent de `currentColor` et suivent donc la teinte de ce qui les porte
 * — pastille de repas, bouton, ligne de réglage — sans une ligne de code de
 * plus.
 *
 * 🔴 **Tous sont `aria-hidden`.** Un glyphe n'est jamais le seul porteur d'une
 * information : le libellé texte à côté de lui, ou l'`aria-label` du bouton qui
 * le contient, portent seuls le sens. C'est la règle des icônes d'onglets
 * depuis l'étape 5, et c'est aussi ce qui rend la couleur des pastilles de
 * repas purement décorative.
 *
 * ⚠️ `react-refresh` interdit à un module de composants d'exporter autre
 * chose : le choix de la teinte d'un repas vit donc dans
 * `screens/today/mealTone.ts`, pas ici.
 */

import type { ReactNode } from 'react';

/**
 * Enveloppe commune — grille 24, contour seul, extrémités arrondies.
 *
 * ⚠️ L'épaisseur du trait varie selon l'usage dans le prototype (2 pour les
 * pastilles de réglages, 2,2 pour les icônes de repas, 2,4 pour les chevrons,
 * 2,6 pour le « + »). Elle est donc un paramètre, avec le défaut le plus
 * courant.
 */
function Stroke({
  children,
  size = 18,
  width = 2.2,
}: {
  children: ReactNode;
  size?: number;
  width?: number;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={width}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/* ==========================================================================
 * Navigation et actions
 * ======================================================================= */

/** Chevron droit — plie et déplie une fiche repas, indique une sous-page. */
export function GlyphChevronRight({ size = 16 }: { size?: number }) {
  return (
    <Stroke size={size} width={2.4}>
      <path d="M9 6l6 6-6 6" />
    </Stroke>
  );
}

/** Chevron gauche — jour précédent, retour au sommaire. */
export function GlyphChevronLeft({ size = 16 }: { size?: number }) {
  return (
    <Stroke size={size} width={2.4}>
      <path d="M15 18l-6-6 6-6" />
    </Stroke>
  );
}

/** Plus — ajouter une entrée à un repas, un objet à la bibliothèque. */
export function GlyphPlus({ size = 16 }: { size?: number }) {
  return (
    <Stroke size={size} width={2.6}>
      <path d="M12 5v14M5 12h14" />
    </Stroke>
  );
}

/** Croix — retirer une entrée du journal. */
export function GlyphClose({ size = 14 }: { size?: number }) {
  return (
    <Stroke size={size} width={2.4}>
      <path d="M6 6l12 12M18 6L6 18" />
    </Stroke>
  );
}

/**
 * Trois points — le menu d'un repas (prototype : le caractère « ⋯ »).
 *
 * ⚠️ Tracé plutôt que typographié : « ⋯ » n'a pas la même chasse ni la même
 * hauteur d'un système à l'autre, et il ne se centre pas dans un bouton rond
 * de la même façon sur iOS et sur Linux — c'est ce qui décalait le glyphe au
 * banc à l'étape 11.
 */
export function GlyphMore({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="5" cy="12" r="1.9" />
      <circle cx="12" cy="12" r="1.9" />
      <circle cx="19" cy="12" r="1.9" />
    </svg>
  );
}

/**
 * Corbeille — l'action révélée en glissant une entrée du journal vers la
 * gauche.
 *
 * ⚠️ **Elle ne figure pas dans le prototype**, qui ne connaît pas ce geste : la
 * maquette pose une croix ronde au bout de chaque ligne. Le glissement l'a
 * remplacée après essai sur l'appareil. Le glyphe suit le même tracé que les
 * autres — grille 24, contour seul, extrémités arrondies — pour ne pas trahir
 * la famille.
 *
 * ⚠️ Comme les autres, il est `aria-hidden` : c'est le mot « Supprimer » à côté
 * de lui, et l'`aria-label` du bouton, qui portent le sens.
 */
export function GlyphTrash({ size = 16 }: { size?: number }) {
  return (
    <Stroke size={size} width={2}>
      <path d="M4 7h16" />
      <path d="M9 7V4.6a.6.6 0 0 1 .6-.6h4.8a.6.6 0 0 1 .6.6V7" />
      <path d="M6.5 7l.8 12.4a1.6 1.6 0 0 0 1.6 1.6h6.2a1.6 1.6 0 0 0 1.6-1.6L17.5 7" />
      <path d="M10.5 11v6M13.5 11v6" />
    </Stroke>
  );
}

/** Loupe — l'ornement de tête d'un champ de recherche. */
export function GlyphSearch({ size = 16 }: { size?: number }) {
  return (
    <Stroke size={size} width={2.2}>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.3-4.3" />
    </Stroke>
  );
}

/**
 * Étoile de favori.
 *
 * ⚠️ **Deux tracés, pas deux couleurs** : plein quand c'est un favori, contour
 * sinon. L'état ne repose donc pas sur la teinte seule — même discipline que
 * partout ailleurs.
 */
export function GlyphStar({ filled, size = 18 }: { filled: boolean; size?: number }) {
  const path = 'M12 2l2.9 6.9L22 9.6l-5.4 4.9L18 22l-6-3.6L6 22l1.4-7.5L2 9.6l7.1-.7L12 2z';
  if (filled) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
        focusable="false"
      >
        <path d={path} />
      </svg>
    );
  }
  return (
    <Stroke size={size} width={1.8}>
      <path d={path} />
    </Stroke>
  );
}

/* ==========================================================================
 * Icônes de repas — les cinq du prototype
 * ======================================================================= */

/** Éclair — pré-entraînement. */
export function GlyphBolt({ size = 18 }: { size?: number }) {
  return (
    <Stroke size={size}>
      <path d="M13 2L4 14h6l-1 8 9-12h-6l1-8z" />
    </Stroke>
  );
}

/**
 * Triangle de lecture — **démarrer une séance depuis la liste des routines**
 * *(31/08/2026, demandé après usage)*.
 *
 * ⚠️ **Plein, et non en contour**, contrairement à tous ses voisins : un
 * triangle de 12 px tracé au trait se lit comme un chevron, c'est-à-dire comme
 * « ouvrir » — précisément l'autre action de la ligne. Le remplissage est ce
 * qui le distingue du geste avec lequel il ne doit pas être confondu.
 */
export function GlyphPlay({ size = 15 }: { size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden
      focusable="false"
    >
      <path d="M8 5.5v13l11-6.5z" fill="currentColor" />
    </svg>
  );
}

/** Soleil — petit-déjeuner. */
export function GlyphSun({ size = 18 }: { size?: number }) {
  return (
    <Stroke size={size}>
      <circle cx="12" cy="12" r="4.5" />
      <path d="M12 2.5v2M12 19.5v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M2.5 12h2M19.5 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4" />
    </Stroke>
  );
}

/** Couvert — déjeuner, et l'icône générique d'un repas sans heure connue. */
export function GlyphCutlery({ size = 18 }: { size?: number }) {
  return (
    <Stroke size={size}>
      <path d="M7 2v8M4 2v4a3 3 0 003 3M10 2v4a3 3 0 01-3 3M17 2c-2 3-2 6 0 8v12" />
    </Stroke>
  );
}

/** Pomme — collation. */
export function GlyphApple({ size = 18 }: { size?: number }) {
  return (
    <Stroke size={size}>
      <path d="M12 8c4 0 6.5 3 6.5 7.5S15 21 12 21s-6.5-2-6.5-5.5S8 8 12 8z" />
      <path d="M12 8c0-2.5 1-4.5 2.5-5.5" />
    </Stroke>
  );
}

/** Lune — dîner. */
export function GlyphMoon({ size = 18 }: { size?: number }) {
  return (
    <Stroke size={size}>
      <path d="M20 12.5A8.5 8.5 0 1111.5 4a6.5 6.5 0 008.5 8.5z" />
    </Stroke>
  );
}

/* ==========================================================================
 * Pastilles des Réglages — les six du prototype
 * ======================================================================= */

export function GlyphTheme({ size = 19 }: { size?: number }) {
  return (
    <Stroke size={size} width={2}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </Stroke>
  );
}

export function GlyphCalendar({ size = 19 }: { size?: number }) {
  return (
    <Stroke size={size} width={2}>
      <rect x="3" y="4" width="18" height="17" rx="3" />
      <path d="M3 9h18M8 2v4M16 2v4" />
    </Stroke>
  );
}

export function GlyphBell({ size = 19 }: { size?: number }) {
  return (
    <Stroke size={size} width={2}>
      <path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 01-3.4 0" />
    </Stroke>
  );
}

export function GlyphUser({ size = 19 }: { size?: number }) {
  return (
    <Stroke size={size} width={2}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7" />
    </Stroke>
  );
}

export function GlyphSync({ size = 19 }: { size?: number }) {
  return (
    <Stroke size={size} width={2}>
      <path d="M4 12a8 8 0 0114-5.3L21 9M20 12a8 8 0 01-14 5.3L3 15" />
    </Stroke>
  );
}

export function GlyphTrend({ size = 19 }: { size?: number }) {
  return (
    <Stroke size={size} width={2}>
      <path d="M3 17l6-6 4 4 8-8" />
      <path d="M15 6h6v6" />
    </Stroke>
  );
}
